import { Fragment, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { downloadXlsx } from '../utils/exportXlsx';
import ReportShell from './ReportShell';

// Linear footage — what to bill on linear (not drop) tickets. Each ticket's dig area is
// matched to the corridor spans on the EZEE FIBER: WA map; footage is the CALC_LENGTH of
// spans marked PROGRESS = COMPLETE, counted on the span's DATE_COMPLETED. A span covered
// by several re-filed tickets is billed once, on the ticket in force when it was done.

const SURFACE = '#0d1b2e';
const BORDER = '#1e3a5f';
const inputStyle = { backgroundColor: SURFACE, borderColor: BORDER };
const CITIES = [
  { key: 'STEILACOOM', label: 'Steilacoom' },
  { key: 'FIFE', label: 'Fife' },
  { key: 'ALGONA', label: 'Algona' },
];

const TZ = 'America/Los_Angeles';
const todayPacific = () => new Date().toLocaleDateString('en-CA', { timeZone: TZ });
const parseDay = (s) => new Date(`${s}T00:00:00Z`);
const addDays = (s, n) => new Date(parseDay(s).getTime() + n * 86400000).toISOString().slice(0, 10);
const mondayOf = (s) => addDays(s, -((parseDay(s).getUTCDay() + 6) % 7));
const fmtDay = (s) => { if (!s) return '—'; const [y, m, d] = s.split('-'); return `${+m}/${+d}/${y}`; };
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { timeZone: TZ }) : '—');
const ft = (n) => (n == null ? '—' : Math.round(n).toLocaleString('en-US'));
const days = (r) => (r.first_day === r.last_day ? fmtDay(r.first_day) : `${fmtDay(r.first_day)} – ${fmtDay(r.last_day)}`);
const address = (r) => [[r.address, r.street].filter(Boolean).join(' '), r.cross_street && `@ ${r.cross_street}`].filter(Boolean).join(' ');

function presets() {
  const t = todayPacific();
  const mon = mondayOf(t);
  const [y, m] = t.split('-').map(Number);
  const firstThis = `${y}-${String(m).padStart(2, '0')}-01`;
  const lastPrev = addDays(firstThis, -1);
  return {
    'This week': [mon, t],
    'Last week': [addDays(mon, -7), addDays(mon, -1)],
    'This month': [firstThis, t],
    'Last month': [`${lastPrev.slice(0, 7)}-01`, lastPrev],
  };
}

export default function LinearFootageReport({ onClose, embedded = false }) {
  const init = presets()['This month'];
  const [from, setFrom] = useState(init[0]);
  const [to, setTo] = useState(init[1]);
  const [city, setCity] = useState('all');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(() => new Set());

  async function run(f = from, t = to, c = city) {
    if (!f || !t || f > t) { setError('Pick a start date on or before the end date.'); return; }
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/tickets/footage', { params: { from: f, to: t, city: c }, timeout: 120000 });
      setReport(data);
      setOpen(new Set());
    } catch (e) {
      setError(e.response?.data?.error || 'Could not build the report.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { run(); }, []);

  const rows = report?.rows || [];
  const unmatched = report?.unmatched || [];
  const shownCities = city === 'all' ? CITIES : CITIES.filter((c) => c.key === city);
  const totals = useMemo(() => {
    const byCity = {};
    let all = 0;
    rows.forEach((r) => { all += r.feet; byCity[r.city] = (byCity[r.city] || 0) + r.feet; });
    return { all, byCity };
  }, [rows]);

  function toggle(no) {
    setOpen((s) => { const n = new Set(s); if (n.has(no)) n.delete(no); else n.add(no); return n; });
  }

  async function exportExcel() {
    const range = `${report.from}${report.from === report.to ? '' : `-to-${report.to}`}`;
    await downloadXlsx({
      filename: `811-linear-footage-${range}${city !== 'all' ? `-${city.toLowerCase()}` : ''}.xlsx`,
      sheetName: 'Linear Footage',
      headers: ['Ticket #', 'City (Locate For)', 'Ticket Place', 'Address', 'Type of Work', 'Called In', 'Completed (Esri)', 'Completed Ft (billable)',
        'Ticket Says (ft)', 'Corridor in Ticket (ft)', 'Not Complete Yet (ft)', 'Billed on Other Tickets (ft)', 'Spans', 'Completed By (PM_1)',
        'Also Covered By', '811 Status (City)', 'Contractor', 'Ticket Link'],
      rows: [
        ...rows.map((r) => [
          r.ticket_no, r.city, r.place || '', address(r), r.type_of_work || '', fmtDate(r.call_date), days(r), Math.round(r.feet),
          r.ticket_feet ?? '', Math.round(r.corridor_feet), Math.round(r.corridor_open_feet), Math.round(r.elsewhere_feet),
          r.spans.map((s) => `${s.label || `${s.layer} #${s.oid}`} (${Math.round(s.length)} ft)`).join('; '),
          r.pm || '', r.also_on.join(', '), r.city_status || '', r.excavator || '', r.ticket_link || '',
        ]),
        ...(unmatched.length ? [[], ['NO CORRIDOR FOUND — bill from the ticket'], ...unmatched.map((u) => [
          u.ticket_no, u.city, u.place || '', address(u), u.type_of_work || '', fmtDate(u.call_date), `811: ${fmtDate(u.city_completed_at)}`, '',
          u.ticket_feet ?? '', '', '', '', u.reason, '', '', u.city_status || '', u.excavator || '', u.ticket_link || '',
        ])] : []),
      ],
    });
  }

  return (
    <ReportShell embedded={embedded} onClose={onClose} title="Linear Footage" maxWidth="max-w-6xl"
      subtitle="Completed corridor footage (CALC_LENGTH) on linear — not drop — tickets">
      <div className="flex items-center gap-3 px-5 pt-4 pb-3 flex-wrap">
        <label className="text-xs text-gray-400 uppercase tracking-wide">From</label>
        <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="text-sm rounded px-2 py-1.5 border text-white focus:outline-none [color-scheme:dark]" style={inputStyle} />
        <label className="text-xs text-gray-400 uppercase tracking-wide">To</label>
        <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="text-sm rounded px-2 py-1.5 border text-white focus:outline-none [color-scheme:dark]" style={inputStyle} />
        <select value={city} onChange={(e) => setCity(e.target.value)} className="text-sm rounded px-2 py-1.5 border text-white focus:outline-none" style={inputStyle}>
          <option value="all">All cities</option>
          {CITIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <button onClick={() => run()} disabled={loading}
          className="text-xs px-3 py-2 rounded font-bold uppercase tracking-wide transition disabled:opacity-50"
          style={{ backgroundColor: '#c9a84c', color: SURFACE }}>
          {loading ? 'Running…' : 'Run report'}
        </button>
        <button onClick={exportExcel} disabled={!rows.length && !unmatched.length}
          className="text-xs px-3 py-2 rounded border font-bold uppercase tracking-wide text-gray-300 hover:text-white transition disabled:opacity-40"
          style={{ borderColor: BORDER }}>
          Export to Excel
        </button>
        <div className="flex gap-1 ml-auto">
          {Object.entries(presets()).map(([name, [f, t]]) => (
            <button key={name} onClick={() => { setFrom(f); setTo(t); run(f, t); }}
              className="text-[11px] px-2 py-1 rounded border text-gray-400 hover:text-white transition" style={{ borderColor: BORDER }}>
              {name}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-y-auto px-5 pb-5 space-y-4">
        {error && <p className="text-red-400 text-sm">{error}</p>}
        {loading && !report && <p className="text-gray-500 text-sm">Matching tickets to the corridor layers — this can take 20 seconds…</p>}

        {report && (
          <>
            <div className="flex flex-wrap gap-3">
              <Stat label={`Completed ft ${fmtDay(report.from)} – ${fmtDay(report.to)}`} value={ft(totals.all)} />
              {shownCities.map((c) => <Stat key={c.key} label={c.label} value={ft(totals.byCity[c.key] || 0)} />)}
              <Stat label="Tickets" value={rows.length} />
            </div>

            <p className="text-xs text-gray-500">
              A span counts on its DATE_COMPLETED in Esri (or the ticket's 811 completed date when that's blank) and is billed once —
              when re-filed tickets cover the same stretch it goes on the ticket in force when it was completed (the others are listed under "Also covered by").
              {' '}Corridor layers read: {(report.corridorLayers || []).join(', ') || 'none'}.
              {report.undated?.spans > 0 && ` ${report.undated.spans} completed span${report.undated.spans === 1 ? '' : 's'} (${ft(report.undated.feet)} ft) have no completed date anywhere and aren't counted.`}
            </p>

            <section>
              <h3 className="text-xs font-semibold text-gray-300 uppercase tracking-wide mb-2">Tickets ({rows.length})</h3>
              {rows.length === 0 ? (
                <p className="text-gray-500 text-sm">No corridor footage was completed on linear tickets in this range.</p>
              ) : (
                <div className="rounded-lg border overflow-x-auto" style={{ borderColor: BORDER }}>
                  <table className="w-full text-xs">
                    <thead style={{ backgroundColor: '#080f1a' }}>
                      <tr>
                        {['', 'Ticket #', 'City', 'Address', 'Completed', 'Completed ft', 'Ticket says', 'Corridor in ticket', 'Completed by', 'Also covered by'].map((h) => (
                          <th key={h} className={`px-3 py-2 font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap ${/ft|says|Corridor/.test(h) ? 'text-right' : 'text-left'}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <Fragment key={r.ticket_no}>
                          <tr className="border-t hover:bg-white/5 cursor-pointer" style={{ borderColor: BORDER }} onClick={() => toggle(r.ticket_no)}>
                            <td className="pl-3 py-2 text-gray-500">{open.has(r.ticket_no) ? '▾' : '▸'}</td>
                            <td className="px-3 py-2 font-bold whitespace-nowrap">
                              {r.ticket_link
                                ? <a href={r.ticket_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="hover:underline" style={{ color: '#c9a84c' }}>{r.ticket_no}</a>
                                : <span style={{ color: '#c9a84c' }}>{r.ticket_no}</span>}
                            </td>
                            <td className="px-3 py-2 text-gray-200 whitespace-nowrap">{r.city}{r.place && r.place !== r.city && <span className="text-gray-500"> (in {r.place})</span>}</td>
                            <td className="px-3 py-2 text-gray-200">{address(r) || '—'}</td>
                            <td className="px-3 py-2 text-gray-300 whitespace-nowrap">{days(r)}</td>
                            <td className="px-3 py-2 text-right text-white font-bold tabular-nums">{ft(r.feet)}</td>
                            <td className="px-3 py-2 text-right text-gray-400 tabular-nums">{ft(r.ticket_feet)}</td>
                            <td className="px-3 py-2 text-right text-gray-400 tabular-nums whitespace-nowrap" title={`${ft(r.corridor_open_feet)} ft not complete yet; ${ft(r.elsewhere_feet)} ft billed on another ticket`}>
                              {ft(r.corridor_feet)}
                              {r.corridor_open_feet > 0 && <span className="text-yellow-300/80"> · {ft(r.corridor_open_feet)} open</span>}
                            </td>
                            <td className={`px-3 py-2 ${r.pm ? 'text-white' : 'text-gray-500'}`}>{r.pm || '—'}</td>
                            <td className="px-3 py-2 text-gray-500">{r.also_on.length ? r.also_on.join(', ') : '—'}</td>
                          </tr>
                          {open.has(r.ticket_no) && (
                            <tr style={{ backgroundColor: '#080f1a' }}>
                              <td />
                              <td colSpan={9} className="px-3 pb-3 pt-1">
                                <table className="text-[11px]">
                                  <thead><tr className="text-gray-500">
                                    <th className="text-left font-semibold pr-6 pb-1">Span</th>
                                    <th className="text-left font-semibold pr-6 pb-1">Layer</th>
                                    <th className="text-right font-semibold pr-6 pb-1">CALC_LENGTH</th>
                                    <th className="text-left font-semibold pr-6 pb-1">Completed</th>
                                    <th className="text-left font-semibold pb-1">PM_1</th>
                                  </tr></thead>
                                  <tbody>
                                    {r.spans.map((s) => (
                                      <tr key={`${s.layer}#${s.oid}`} className="text-gray-300">
                                        <td className="pr-6">{s.label || `#${s.oid}`}</td>
                                        <td className="pr-6 text-gray-500">{s.layer}</td>
                                        <td className="pr-6 text-right tabular-nums">{ft(s.length)}</td>
                                        <td className="pr-6">{fmtDay(s.completed_day)}{!s.dated && <span className="text-gray-500"> (811 date)</span>}</td>
                                        <td>{s.pm || '—'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                {r.elsewhere_feet > 0 && <p className="text-gray-500 mt-1">{ft(r.elsewhere_feet)} ft of this ticket's completed corridor is billed on another ticket.</p>}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {unmatched.length > 0 && (
              <section>
                <h3 className="text-xs font-semibold text-yellow-300 uppercase tracking-wide mb-1">No corridor found ({unmatched.length})</h3>
                <p className="text-xs text-gray-500 mb-2">Linear tickets the city completed in this range that couldn't be matched to a corridor — bill these from the ticket.</p>
                <div className="rounded-lg border overflow-x-auto" style={{ borderColor: BORDER }}>
                  <table className="w-full text-xs">
                    <thead style={{ backgroundColor: '#080f1a' }}>
                      <tr>
                        {['Ticket #', 'City', 'Address', '811 Completed', 'Ticket says (ft)', 'Why'].map((h) => (
                          <th key={h} className="text-left px-3 py-2 font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {unmatched.map((u) => (
                        <tr key={u.ticket_no} className="border-t" style={{ borderColor: BORDER }}>
                          <td className="px-3 py-2 font-bold whitespace-nowrap">
                            {u.ticket_link
                              ? <a href={u.ticket_link} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: '#c9a84c' }}>{u.ticket_no}</a>
                              : <span style={{ color: '#c9a84c' }}>{u.ticket_no}</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-200 whitespace-nowrap">{u.city}</td>
                          <td className="px-3 py-2 text-gray-200">{address(u) || '—'}</td>
                          <td className="px-3 py-2 text-gray-300 whitespace-nowrap">{fmtDate(u.city_completed_at)}</td>
                          <td className="px-3 py-2 text-white tabular-nums">{ft(u.ticket_feet)}</td>
                          <td className="px-3 py-2 text-gray-400">{u.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </ReportShell>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border px-3 py-2" style={{ borderColor: BORDER, backgroundColor: '#080f1a' }}>
      <div className="text-[11px] text-gray-400 uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-black text-white tabular-nums">{value}</div>
    </div>
  );
}
